import { createContext, use } from "react";

/**
 * Split from session.tsx so that file exports only components.
 *
 * React Fast Refresh is per-module: a file exporting both a provider and a hook
 * cannot hot-reload, so every edit under it reloads the whole app and drops its
 * state. The design package split toast-context.ts for the same reason.
 */
export interface SessionContextValue {
  /**
   * `signing-out` is its own state, not a flavour of signed-out.
   *
   * A signed-out visitor is handed to the sign-in screen automatically. If
   * signing out used that state, the hand-off would fire while the sign-out was
   * still in flight and carry the person straight back in — which is the bug
   * this distinguishes away.
   */
  status: "loading" | "signed-in" | "signed-out" | "signing-out";
  /**
   * Send somebody to control's sign-in screen. `create` opens it on the signup
   * wording — control reads OIDC's own prompt=create, so this is a parameter
   * rather than a second endpoint.
   */
  signIn: (options?: { create?: boolean }) => Promise<void>;
  /**
   * An involuntary 401: this app's session is gone, so show its own sign-in.
   * Never `signOut`, which ends the shared session in every app.
   */
  expire: () => void;
  /**
   * An involuntary 401 came back within seconds of a silent sign-in, so signing
   * in again will not fix it. The hand-off stops and the fallback says why.
   */
  stuck: boolean;
  /**
   * Ends the session and navigates away, so it does not return in any useful
   * sense. Typed as a promise because it awaits the server before leaving —
   * signalling to a caller that firing and forgetting it is not the same thing.
   */
  signOut: () => Promise<void>;
}

export const SessionContext = createContext<SessionContextValue | null>(null);

export function useSession(): SessionContextValue {
  const ctx = use(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside a SessionProvider");
  return ctx;
}

/**
 * Marks that this tab has already been handed off to the sign-in screen.
 *
 * Defined here, beside the session it belongs to, because two copies of the
 * string is a guard that stops guarding the moment one of them is edited — and
 * what it guards against is an infinite redirect between two servers.
 */
export const handedOffKey = "mw.handedOff";

/**
 * Which screen a session status calls for before the application proper.
 *
 * The distinction that matters is between `signed-out` and `signing-out`. The
 * first is handed to the sign-in screen automatically; the second must not be,
 * because the sign-out has not reached the server yet and the hand-off would
 * carry the person straight back in — which is what made signing out look like
 * it did nothing at all.
 */
export function screenFor(
  status: SessionContextValue["status"],
): "loading" | "hand-off" | "waiting" | "app" {
  switch (status) {
    case "loading":
      return "loading";
    case "signed-out":
      return "hand-off";
    case "signing-out":
      return "waiting";
    default:
      return "app";
  }
}

/**
 * Claim an automatic hand-off to the sign-in screen.
 *
 * The hazard this guards is a loop: if a session never sticks — a cookie the
 * browser refuses, a clock skewed far enough that every token reads as expired
 * — then redirecting on signed-out bounces between two servers that each think
 * the other should have dealt with it.
 *
 * It used to allow one hand-off per tab, which cannot tell a loop from an
 * ordinary return. Somebody who signed out and came back met a screen saying
 * "That sign-in did not complete" — untrue, and a dead end. A loop iterates in
 * milliseconds and a person does not, so the honest test is *how recently*
 * rather than *whether*.
 *
 * `now` is a parameter so the rule can be tested without waiting ten seconds.
 */
const handOffFloor = 10_000;

export function claimHandOff(
  store: Pick<Storage, "getItem" | "setItem">,
  now: number = Date.now(),
): boolean {
  const last = Number(store.getItem(handedOffKey) ?? "");
  if (Number.isFinite(last) && last > 0 && now - last < handOffFloor) return false;
  store.setItem(handedOffKey, String(now));
  return true;
}

export function releaseHandOff(store: Pick<Storage, "removeItem">): void {
  store.removeItem(handedOffKey);
  store.removeItem(returnedKey);
}

/**
 * When a hand-off last came back signed in.
 *
 * Releasing the claim on that return is what lets the next sign-out hand off
 * too, and it is also what reopens the loop when the session it brings back is
 * refused at once: 401, hand-off, silent sign-in, released, 401 again. This
 * stamp is how `expireLoops` tells that apart from a session that ran out.
 */
export const returnedKey = "mw.handOffReturned";

const loopWindow = 30_000;

/** An authenticated check: give the claim back, and note if a hand-off just landed. */
export function completeHandOff(
  store: Pick<Storage, "getItem" | "setItem" | "removeItem">,
  now: number = Date.now(),
): void {
  if (store.getItem(handedOffKey) !== null) store.setItem(returnedKey, String(now));
  store.removeItem(handedOffKey);
}

/** Whether an involuntary 401 follows a hand-off's return too closely to try again. */
export function expireLoops(
  store: Pick<Storage, "getItem">,
  now: number = Date.now(),
): boolean {
  const back = Number(store.getItem(returnedKey) ?? "");
  return Number.isFinite(back) && back > 0 && now - back < loopWindow;
}
